import { Component, OnDestroy, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { DataService } from '../data.service';
import { KeretService } from '../keret.service';
import { modeNev } from '../mode-nevek';
import { temaNev } from '../modul-tema';

/**
 *  PROFIL – a bejelentkezett felhasználó SAJÁT adatai, csak olvasásra (2026-11-02).
 *
 *  A user kérése: „ott lássa mindenki a saját adatait, profilképét … módosítani egyelőre ne
 *  tudjanak semmit. Szülő lássa a gyermekek adatait is." Az adat a `/api/sajat-profil`-ból jön
 *  (backend/sajat-profil.js): a kilétet és a gyermekeket a kiszolgáló a TOKENBŐL dönti el, a
 *  képernyő nem küld azonosítót. Egy ember több szerepben is lehet (munkatárs és szülő) – amit
 *  a kiszolgáló visszaad, az mind látszik.
 *
 *  A korábbi „Jelszó módosítása" / „Név és e-mail módosítása" gomb (Keycloak kc_action,
 *  2026-09-29) KIKERÜLT: a user kérésére a profilban egyelőre semmi nem módosítható, a képernyő
 *  csak megmondja, hol kérheti a felhasználó.
 *
 *  A személyes adatok (születés, anyja neve, lakcím) alapból rejtve: a profil kivetítőn vagy
 *  közös gépen is megnyílhat. Egy koppintás megmutatja – ez csak a nézet, nem módosítás.
 */
@Component({
  selector: 'app-profil',
  templateUrl: './profil.component.html',
  styleUrls: ['./profil.component.scss'],
})
export class ProfilComponent implements OnInit, OnDestroy {
  betolt = true;
  hiba = '';
  p: any = null;
  loginmode = '';
  szemelyesLathato = false;
  /** oktatási azonosító → a tanuló (vagy gyermek) profilképének blob-URL-je; null = nincs kép. */
  kepek: Record<string, string | null> = {};

  constructor(private http: HttpClient, private data: DataService, public keret: KeretService) {}

  async ngOnInit() {
    this.loginmode = String((await this.data.getFromStorage('loginmode').catch(() => '')) || '');
    await this.frissit();
  }

  ngOnDestroy() {
    for (const u of Object.values(this.kepek)) if (u) URL.revokeObjectURL(u);
  }

  async frissit() {
    this.betolt = true;
    this.hiba = '';
    try {
      this.p = await firstValueFrom(this.http.get<any>('/api/sajat-profil'));
      const tanulok = [this.p?.tanulo, ...(this.p?.gyermekek || [])].filter((t) => t?.oktazon);
      for (const t of tanulok) this.kepBetolt(String(t.oktazon));
    } catch (e: any) {
      this.p = null;
      this.hiba = e?.status === 401 || e?.status === 403
        ? 'A profil megtekintéséhez újra be kell jelentkezned.'
        : (e?.error?.error || 'A profil adatai most nem tölthetők be. Próbáld újra később.');
    } finally {
      this.betolt = false;
    }
  }

  /*  A tanuló/gyermek képe tokennel, blobként (egy sima <img src> nem visz belépési jegyet). Ha
   *  nincs Microsoft-fotó vagy ki van kapcsolva, 204 jön – ilyenkor a monogram marad. */
  private async kepBetolt(oktazon: string) {
    if (oktazon in this.kepek) return;
    this.kepek[oktazon] = null;
    try {
      const r = await firstValueFrom(this.http.get(`/api/sajat-profil/kep/${encodeURIComponent(oktazon)}`,
        { responseType: 'blob', observe: 'response' }));
      if (r.status === 200 && r.body && r.body.size > 0) this.kepek[oktazon] = URL.createObjectURL(r.body);
    } catch { /* nincs kép – a monogram marad */ }
  }

  get dolgozo(): any { return this.p?.dolgozo || null; }
  get gondviselo(): any { return this.p?.gondviselo || null; }
  get tanulo(): any { return this.p?.tanulo || null; }
  get gyermekek(): any[] { return this.p?.gyermekek || []; }
  get szerepek(): string[] { return this.p?.szerepek || []; }

  get nev(): string {
    return this.dolgozo?.nev || this.tanulo?.nev || this.gondviselo?.nev || this.p?.fiok?.nev
      || this.keret.a?.nev() || this.p?.fiok?.felhasznalonev || 'Saját fiók';
  }

  /** A nagy kép: tanulói belépésnél a saját (Microsoft) fotója, egyébként ugyanaz, mint a fejlécben. */
  get sajatKep(): string {
    const t = this.tanulo?.oktazon ? this.kepek[String(this.tanulo.oktazon)] : null;
    return t || this.keret.a?.avatarUrl() || '';
  }
  kepHiba(ev: Event) { this.keret.a?.avatarHiba(ev); }

  get email(): string {
    return this.p?.fiok?.email || this.dolgozo?.email || this.tanulo?.email || this.gondviselo?.email || '';
  }
  get telefon(): string {
    return this.dolgozo?.telefon || this.tanulo?.telefon || this.gondviselo?.telefon || '';
  }

  szerepNev(s: string): string {
    return s === 'dolgozo' ? 'Munkatárs' : s === 'gondviselo' ? 'Szülő / gondviselő' : s === 'tanulo' ? 'Tanuló' : s;
  }

  /** Melyik szerepben lépett be most (a menü is ebből épül). */
  get belepesiSzerep(): string {
    const m = this.loginmode;
    return m === 'gondviselo' || m === 'tanulo' || m === 'dolgozo' ? this.szerepNev(m) : '';
  }

  get aktivIntezmeny(): string { return this.keret.a?.intezmenyNev() || ''; }
  get aktivModul(): string { return modeNev(this.keret.a?.aktualisMod()); }
  get tema(): string { return temaNev(this.keret.a?.tema()); }

  get microsoftos(): boolean { return this.p?.fiok?.belepes?.mod === 'microsoft' || /^kulso-.*(microsoft|azure|entra)/.test(String(this.p?.fiok?.belepes?.mod || '')); }

  /** A jogkörök alrendszerenként csoportosítva: [{ modul: 'Iskolai rendszer', nevek: 'Admin, Pedagógus' }]. */
  jogkorCsoportok(jogkorok: any[]): { modul: string; nevek: string }[] {
    const csop = new Map<string, string[]>();
    for (const j of jogkorok || []) {
      const m = j?.alrendszer ? modeNev(j.alrendszer) : '';
      const l = csop.get(m) || [];
      const n = String(j?.nev || j?.kod || '');
      if (n && !l.includes(n)) l.push(n.charAt(0).toUpperCase() + n.slice(1));
      csop.set(m, l);
    }
    return [...csop.entries()].map(([modul, nevek]) => ({ modul, nevek: nevek.join(', ') }));
  }

  monogram(nev: string): string {
    const sz = String(nev || '').replace(/\b(dr|ifj|id)\.?\s+/gi, '').trim().split(/\s+/).filter(Boolean);
    return sz.length ? (sz[0][0] + (sz.length > 1 ? sz[1][0] : '')).toUpperCase() : '?';
  }

  datum(d: string | null | undefined): string {
    if (!d) return '';
    const [e, h, n] = String(d).slice(0, 10).split('-');
    return e && h && n ? `${e}. ${h}. ${n}.` : String(d);
  }

  szuletes(x: any): string {
    return [x?.szul_hely, this.datum(x?.szul_datum)].filter(Boolean).join(', ');
  }

  vanSzemelyes(x: any): boolean {
    return !!(x?.szuletesi_nev || x?.szul_hely || x?.szul_datum || x?.anyja_neve || x?.allando_lakcim || x?.tartozkodasi_hely);
  }
}
